import { CustomModal, FormEntry, CustomAlert } from "components";
import { confirmationModalInitialState, confirmationModalReducer, confirmationModalReducerActions } from "components/CustomAlert/reducers";
import { Button } from "reactstrap";
import { useEffect, useState, useReducer, useRef } from "react";
import { useForm } from "react-hook-form";
import { useMutation } from "react-query";
import apiService from "services/api";
import { tConv24 } from "utils/dateHelper";

export const eventTypes = {
	ATTENDANCE: "Attendance",
	STAFF_APPLICATION: "Staff Application",
};

const StaffAvailabilityModal = ({ state, dispatch, refetch }) => {
	const [editable, setEditable] = useState(false);
	const [deleteRecurred, setDeleteRecurred] = useState(false);
	const placeholderFormButtonRef = useRef(null);

	const { register, handleSubmit, reset } = useForm({ mode: "onBlur" });

	const hideModal = () => {
		dispatch({ type: "close" });
		setDeleteRecurred(false);
		closeDeleteAvailabilityConfirmationModal();
		closeUpdateAvailabilityConfirmationModal();
		setEditable(false);
		refetch();
	};

	const { mutate: commitUpdate, isLoading: isComittingUpdate } = useMutation(
		(data) => apiService.staff.availability.update(state?.data?.staffAvailability?.id, { ...data, deleteRecurred }),
		{
			onSuccess: hideModal,
		}
	);

	const { mutate: commitDelete, isLoading: isCommitingDelete } = useMutation(
		() => apiService.staff.availability.delete(state?.data?.staffAvailability?.id, { delete_recurred: deleteRecurred }), //snake case deleteRecurred because query params accept snake case
		{
			onSuccess: hideModal,
		}
	);

	const [deleteAvailabilityConfirmationModalProps, deleteAvailabilityConfirmationDispatch] = useReducer(
		confirmationModalReducer,
		confirmationModalInitialState
	);

	const triggerDeleteAvailabiltyConfirmationModal = () => {
		deleteAvailabilityConfirmationDispatch({
			type: confirmationModalReducerActions.OPEN,
		});
	};

	const closeDeleteAvailabilityConfirmationModal = () =>
		deleteAvailabilityConfirmationDispatch({
			type: confirmationModalReducerActions.CLOSE,
		});

	const [updateAvailabilityConfirmationModalProps, updateAvailabilityConfirmationDispatch] = useReducer(
		confirmationModalReducer,
		confirmationModalInitialState
	);

	const triggerUpdateAvailabiltyConfirmationModal = () => {
		updateAvailabilityConfirmationDispatch({
			type: confirmationModalReducerActions.OPEN,
		});
	};

	const closeUpdateAvailabilityConfirmationModal = () =>
		updateAvailabilityConfirmationDispatch({
			type: confirmationModalReducerActions.CLOSE,
		});

	useEffect(() => {
		if (state?.data?.staffAvailability) reset(state?.data?.staffAvailability);
	}, [state.data]);

	return (
		<>
			<CustomAlert
				show={deleteAvailabilityConfirmationModalProps.show}
				hideModal={closeDeleteAvailabilityConfirmationModal}
				title="Confirmation"
				disabled={isCommitingDelete}
				description={
					<div>
						<span className="text-warning">Confirm Delete Availability?</span>
						{state?.data?.staffAvailability?.recurrenceId && (
							<div className={`form-check mt-3`}>
								<label className="form-check-label">
									<input
										className="form-check-input"
										type="checkbox"
										onChange={(e) => {
											setDeleteRecurred(e.target.checked);
										}}
										checked={deleteRecurred}
										defaultChecked={false}
									/>
									<span className="form-check-sign"></span>
									Delete Recurred Records?
								</label>
							</div>
						)}
					</div>
				}
				submitButtonText="Delete"
				submitModal={commitDelete}
				fitToParent={true}
			/>

			<CustomAlert
				show={updateAvailabilityConfirmationModalProps.show}
				hideModal={closeUpdateAvailabilityConfirmationModal}
				title="Confirmation"
				disabled={isCommitingDelete}
				description={
					<span>
						<span className="text-warning">Confirm Update Availability?</span>
						{state?.data?.staffAvailability?.recurrenceId && (
							<span className={`form-check mt-3`}>
								<label className="form-check-label">
									<input
										className="form-check-input"
										type="checkbox"
										onChange={(e) => {
											setDeleteRecurred(e.target.checked);
										}}
										checked={deleteRecurred}
									/>
									<span className="form-check-sign"></span>
									Update Recurred Records?
								</label>
							</span>
						)}
					</span>
				}
				submitButtonText="Update"
				submitModal={() => placeholderFormButtonRef.current?.click()}
				fitToParent={true}
			/>

			<CustomModal
				title="Staff Availability"
				show={state?.open}
				hideModal={hideModal}
				compact={true}
				buttons={
					editable && (
						<>
							<Button
								color="warning"
								form="editStaffAvailabilityForm"
								type="button"
								onClick={triggerUpdateAvailabiltyConfirmationModal}
								disabled={isComittingUpdate || isCommitingDelete}
							>
								Save
							</Button>
							<Button color="secondary" onClick={() => setEditable(false)} disabled={isComittingUpdate || isCommitingDelete}>
								Cancel
							</Button>
						</>
					)
				}
			>
				<form id="editStaffAvailabilityForm" onSubmit={handleSubmit((data) => commitUpdate(data))}>
					<button className="d-none" ref={placeholderFormButtonRef} type="submit">
						Placeholder Button
					</button>
					{!editable && (
						<div className="text-right mx-4">
							<Button
								color="info"
								size="sm"
								type="button"
								onClick={() => setEditable(true)}
								disabled={isComittingUpdate || isCommitingDelete}
							>
								Edit
							</Button>
							<Button
								color="danger"
								size="sm"
								onClick={triggerDeleteAvailabiltyConfirmationModal}
								disabled={isComittingUpdate || isCommitingDelete}
							>
								Delete
							</Button>
						</div>
					)}
					<div className="row text-white px-4 text-left">
						<FormEntry
							formLabel="Date"
							register={register}
							name="date"
							type="date"
							required={true}
							editable={editable}
							alt={state?.data?.staffAvailability?.date}
						/>
						<FormEntry
							formLabel="Start Time"
							register={register}
							name="startTime"
							type="time"
							required={true}
							editable={editable}
							alt={tConv24(state?.data?.staffAvailability?.startTime)}
						/>
						<FormEntry
							formLabel="End Time"
							register={register}
							name="endTime"
							type="time"
							required={true}
							editable={editable}
							alt={tConv24(state?.data?.staffAvailability?.endTime)}
						/>
					</div>
				</form>
			</CustomModal>
		</>
	);
};

export default StaffAvailabilityModal;
